import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { editVideoReducer } from './editVideoReducer';
import {
	EditVideoContext,
	editVideoInitialState,
	TEditVideoSelectedItem,
	TEditVideoVisibleForm,
	TTypeOfEditVideoModalWindow,
} from './editVideoContext';
import { EditVideoActionTypes } from './editVideoActions';
import {
	IMenuTranslation,
	IItemForGetShortInfo,
	TFileId,
	TLanguageIDs,
	IFileData,
	IEditVideo,
	IGetVideoInfo,
} from 'types';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getVideoInfo } from 'backend/videos';

const EditVideoState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editVideoReducer, editVideoInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const videoID = params.get('videoID');

	const { response, loading, refetch } = useFetchData<IGetVideoInfo, string>(
		videoID ? getVideoInfo : null,
		videoID || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditVideoActionTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useNamedHeader(response?.data.video.name);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditVideoActionTypes.SET_ALL_DATA,
				payload: {
					videoID: response.data.video.id,
					IDSelectedStudio: response.data.video.videoStudioId,
					name: response.data.video.name || '',
					IDSelectedCategory: response.data.video.videoCategoryId,
					IDsSelectedLanguages: response.data.video.translations.map((el) => el.languageId),
					translations: response.data.video.translations,
					iconPath: response.data.video.iconPath || '',
					videoPath: response.data.video.videoPath || '',
				},
			});
			dispatch({
				type: EditVideoActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						IDSelectedStudio: response.data.video.videoStudioId,
						name: response.data.video.name || '',
						IDSelectedCategory: response.data.video.videoCategoryId,
						IDsSelectedLanguages: response.data.video.translations.map((el) => el.languageId),
						translations: response.data.video.translations,
						iconFilePath: response.data.video.iconPath || '',
						videoFileData: { fileId: '', fileName: '', fileHash: null },
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditVideoActionTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	}, []);

	const changeVisibleForm = useCallback((value: TEditVideoVisibleForm) => {
		dispatch({
			type: EditVideoActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: EditVideoActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const deletePhoto = useCallback(() => dispatch({ type: EditVideoActionTypes.DELETE_PHOTO }), []);

	const setFetchedStudios = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditVideoActionTypes.SET_FETCHED_STUDIOS,
			payload: {
				studios: value,
			},
		});
	}, []);

	const setIDStudio = useCallback((value: TEditVideoSelectedItem) => {
		dispatch({
			type: EditVideoActionTypes.SET_ID_STUDIO,
			payload: {
				IDStudio: value,
			},
		});
	}, []);

	const refetchStudios = useCallback((needRefetch: boolean) => {
		dispatch({
			type: EditVideoActionTypes.REFETCH_STUDIOS,
			payload: {
				needRefetch,
			},
		});
	}, []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: EditVideoActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditVideoActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				categories: value,
			},
		});
	}, []);

	const setIDCategory = useCallback((value: TEditVideoSelectedItem) => {
		dispatch({
			type: EditVideoActionTypes.SET_ID_CATEGORY,
			payload: {
				IDCategory: value,
			},
		});
	}, []);

	const changeFileData = useCallback((value: IFileData) => {
		dispatch({
			type: EditVideoActionTypes.CHANGE_FILE_DATA,
			payload: {
				fileData: value,
			},
		});
	}, []);

	const setFetchedLanguages = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditVideoActionTypes.SET_FETCHED_LANGUAGES,
			payload: {
				languages: value,
			},
		});
	}, []);

	const setIDsLanguages = useCallback(
		(value: TLanguageIDs[]) => {
			dispatch({
				type: EditVideoActionTypes.SET_IDs_LANGUAGES,
				payload: {
					IDsLanguages: value,
				},
			});

			const isNewLanguageAdded = value.length > state.translations.length;
			if (isNewLanguageAdded) {
				const existingTranslationIDs = state.translations.map((el) => el.languageId);
				const newTranslationID = value.find((el) => !existingTranslationIDs.includes(el));
				if (newTranslationID) {
					const newTranslation: IMenuTranslation = {
						languageId: newTranslationID,
						name: '',
					};
					const updatedTranslations = [...state.translations, newTranslation];
					dispatch({
						type: EditVideoActionTypes.SET_TRANSLATION_FIELDS,
						payload: {
							updatedTranslations,
						},
					});
				}
			} else {
				const updatedTranslations = state.translations.filter((el) => value.includes(el.languageId));
				dispatch({
					type: EditVideoActionTypes.SET_TRANSLATION_FIELDS,
					payload: {
						updatedTranslations,
					},
				});
			}
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: IEditVideo | null) => {
		dispatch({
			type: EditVideoActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				data: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditVideoModalWindow) => {
		dispatch({
			type: EditVideoActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: EditVideoActionTypes.SET_TRANSLATION_TAB,
			payload: {
				translationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditVideoActionTypes.CHANGE_TRANSLATION,
				payload: {
					updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const resetFields = useCallback(() => dispatch({ type: EditVideoActionTypes.RESET_FIELDS }), []);

	return (
		<EditVideoContext.Provider
			value={{
				refetchData,
				changeVisibleForm,
				setFileId,
				deletePhoto,
				setFetchedStudios,
				setIDStudio,
				refetchStudios,
				changeName,
				setFetchedCategories,
				setIDCategory,
				changeFileData,
				setFetchedLanguages,
				setIDsLanguages,
				setDataForSubmit,
				changeTypeOfModalWindow,
				setTranslationTab,
				changeNameTranslation,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				videoID: state.videoID,
				visibleForm: state.visibleForm,
				iconFilePath: state.iconFilePath,
				iconFileId: state.iconFileId,
				fetchedStudios: state.fetchedStudios,
				IDSelectedStudio: state.IDSelectedStudio,
				needRefetchStudios: state.needRefetchStudios,
				name: state.name,
				fetchedCategories: state.fetchedCategories,
				IDSelectedCategory: state.IDSelectedCategory,
				videoFilePath: state.videoFilePath,
				videoFileData: state.videoFileData,
				fetchedLanguages: state.fetchedLanguages,
				IDsSelectedLanguages: state.IDsSelectedLanguages,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
			}}>
			{children}
		</EditVideoContext.Provider>
	);
};

export default EditVideoState;
